import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  Table,
  TabPane,
} from "reactstrap";
import { confirmWrapper } from "../ui/askConfirm";
import EnterpriseService from "../../common/services/enterprise-service";
import LoadingOverlay from "react-loading-overlay";
import { EnterpriseContext } from "./EnterpriseContext";
import { toast, ToastContainer } from "react-toastify";
import Tips from "../Tips";
import { Link, useNavigate } from "react-router-dom";
import truckyService from "../../common/services/trucky-service";
import CustomBadge from "../ui/CustomBadge";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import Paginator from "../ui/Paginator";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";

const ApplicationsManagement = ({ container }) => {
  const { globalContext } = useContext(EnterpriseContext);

  const company = globalContext.company;

  const es = new EnterpriseService();
  const [loadingData, setLoadingData] = useState(false);
  const [applications, setApplications] = useState([]);
  const navigate = useNavigate();
  const [bans, setBans] = useState([]);
  const [showBansDialog, toggleBansDialog] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, perPage: 25 });
  const [totalPages, setTotalPages] = useState(0);
  const [searchParameters, setSearchParameters] = useState({
    name: "",
    status: "",
    discord: "",
  });

  useEffect(() => {
    truckyService.setDocumentTitle("Applications");
  }, []);

  if (!es.can(globalContext.member, "members.manage_applications")) {
    return;
  }

  useEffect(() => {
    getApplications();
  }, []);

  useEffect(() => {
    getApplications();
  }, [pagination.page, pagination.perPage]);

  useEffect(() => {
    getBans();
  }, [showBansDialog]);

  const getApplications = async () => {
    setLoadingData(true);
    const result = await es.getApplications(
      company,
      pagination,
      searchParameters
    );
    setLoadingData(false);

    if (!result.message) {
      setApplications(result.data);
      setTotalPages(result.last_page);
    }
  };

  const handlePageClick = (event) => {
    setPagination({ ...pagination, page: event.selected + 1 });
  };

  const getStatusBadge = (status) => {
    var color = "warning";

    if (status == "accepted") color = "success";
    if (status == "rejected") color = "danger";
    if (status == "in_progress") color = "info";
    if (status == "retired") color = "danger";

    return (
      <CustomBadge size="sm" color={color}>
        {container.localeManager.strings[status]}
      </CustomBadge>
    );
  };

  const getBans = async () => {
    const result = await es.bans(company.id);
    if (!result.message) setBans(result);
  };

  const ban = async (user_id) => {
    if (
      await confirmWrapper(container.localeManager.strings.banConfirmation, {
        container: container,
      })
    ) {
      setLoadingData(true);
      const result = await es.addBan(company.id, user_id);

      if (result.success) {
        toast.success(container.localeManager.strings.userHasBeenBanned);
      } else {
        toast.error(result.message);
      }
      setLoadingData(false);
    }
  };

  const removeBan = async (ban_id) => {
    const result = await es.removeBan(company.id, ban_id);
    if (result.success) getBans();
    else toast.error(result.message);
  };

  const handleRowsPerPageChange = (perPage) => {
    setPagination({ ...pagination, page: 1, perPage: perPage });
  };

  return (
    <LoadingOverlay
      active={loadingData}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/applications"} container={container}></VTCMenu>

        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        to: "/company/members",
                        title: container.localeManager.strings.members,
                      },
                      {
                        title: container.localeManager.strings.applications,
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="page-hero mb-4">
                    <span className="page-hero-icon">
                      <FontAwesomeIcon icon="file-signature" className="mx-0" />
                    </span>
                    <div className="flex-1">
                      <h1 className="page-hero-title mb-0">{container.localeManager.strings.applications}</h1>
                    </div>
                    <div className="ms-auto d-flex gap-2 flex-wrap">
                      <Button color="danger" onClick={() => toggleBansDialog(true)} size="sm">
                        <FontAwesomeIcon icon="ban" className="me-1" />{container.localeManager.strings.bans}
                      </Button>
                      <Link className="btn btn-sm btn-secondary" to="/applications/questionnaire">
                        <FontAwesomeIcon icon="clipboard-question" className="me-1" />{container.localeManager.strings.questionnaire}
                      </Link>
                      <Link className="btn btn-sm btn-secondary" to="/invites">
                        <FontAwesomeIcon icon="arrow-right-to-bracket" className="me-1" />Invites
                      </Link>
                    </div>
                  </div>

                  {!loadingData && applications.length == 0 && (
                    <div className="notice d-flex bg-light-danger rounded border-danger border border-dashed p-6 my-3">
                      {container.localeManager.strings.noNewApplicationsHere}
                    </div>
                  )}
                  <div className="filters-toolbar mt-3 mb-3">
                  <FormGroup row>
                    <Col>
                      <Label>{container.localeManager.strings.username}</Label>
                      <Input
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            name: e.target.value,
                          })
                        }
                        value={searchParameters.name}
                      ></Input>
                    </Col>
                    <Col>
                      <Label>{container.localeManager.strings.status}</Label>
                      <Input
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            status: e.target.value,
                          })
                        }
                        value={searchParameters.status}
                        type="select"
                      >
                        <option value="">
                          {container.localeManager.strings.all}
                        </option>
                        <option value="accepted">
                          {container.localeManager.strings.accepted}
                        </option>
                        <option value="rejected">
                          {container.localeManager.strings.rejected}
                        </option>
                        <option value="retired">
                          {container.localeManager.strings.retired}
                        </option>
                      </Input>
                    </Col>
                    <Col>
                      <Label>{container.localeManager.strings.discord}</Label>
                      <Input
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            discord: e.target.value,
                          })
                        }
                        value={searchParameters.discord}
                      ></Input>
                    </Col>
                    <Col className="d-flex align-items-end">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={getApplications}
                      >
                        {container.localeManager.strings.search}
                      </Button>
                    </Col>
                  </FormGroup>
                  </div>
                  {!loadingData && applications.length > 0 && (
                    <>
                      <Paginator
                        rowsPerPageSelected={pagination.perPage}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        showRowsPerPage={true}
                        forcePage={pagination.page - 1}
                        totalPages={totalPages}
                        handlePageClick={handlePageClick}
                      ></Paginator>
                      <Table
                        className="table-row-dashed table-row-gray-300 align-middle gs-0 mt-3"
                        striped
                        size="sm"
                        responsive
                        hover
                      >
                        <thead>
                          <tr className="fw-bolder text-gray-700 bg-light">
                            <th scope="col" className="ps-4">
                              #
                            </th>
                            <th scope="col">
                              {container.localeManager.strings.date}
                            </th>
                            <th scope="col">
                              {container.localeManager.strings.user}
                            </th>
                            <th scope="col">
                              {container.localeManager.strings.status}
                            </th>
                            <th scope="col">
                              {container.localeManager.strings.recruiter}
                            </th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {applications.map((m) => {
                            return (
                              <tr>
                                <td className="ps-1">{m.id}</td>
                                <td>
                                  {container.localeManager
                                    .moment(m.created_at)
                                    .format("l")}
                                </td>
                                <td>
                                  {m.user.steam_profile != null && (
                                    <a
                                      title={
                                        container.localeManager.strings
                                          .steamProfile
                                      }
                                      target="_blank"
                                      href={`https://steamcommunity.com/profiles/${m.user.steam_profile.steam_id}`}
                                    >
                                      <FontAwesomeIcon
                                        icon={["fab", "steam"]}
                                      ></FontAwesomeIcon>
                                    </a>
                                  )}
                                  {
                                    <Link
                                      to={`/user/${m.user.id}`}
                                      target="_blank"
                                    >
                                      {m.user.name}
                                    </Link>
                                  }
                                </td>
                                <td>{getStatusBadge(m.status)}</td>
                                <td>{m.editor != null ? m.editor.name : ""}</td>
                                <td className="text-end" style={{ width: 150 }}>
                                  <Link
                                    to={`/company/applications/${m.id}`}
                                    className="btn btn-sm btn-icon w-30px h-30px ms-auto"
                                    color="active-light-primary"
                                    title={
                                      container.localeManager.strings.accept
                                    }
                                  >
                                    <FontAwesomeIcon
                                      className="me-0"
                                      icon="pencil-alt"
                                    ></FontAwesomeIcon>
                                  </Link>
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      navigate(
                                        `/messenger?user_id=${m.user.id}`
                                      )
                                    }
                                    className="ms-3 btn-icon w-30px h-30px ms-auto"
                                    color="active-light-primary"
                                    title={
                                      container.localeManager.strings
                                        .directMessage
                                    }
                                  >
                                    <FontAwesomeIcon
                                      className="me-0"
                                      icon="comment-dots"
                                    ></FontAwesomeIcon>
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => ban(m.user.id)}
                                    className="ms-3 btn-icon w-30px h-30px ms-auto"
                                    color="active-light-danger"
                                    title={container.localeManager.strings.ban}
                                  >
                                    <FontAwesomeIcon
                                      className="me-0"
                                      icon="ban"
                                    ></FontAwesomeIcon>
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      <Paginator
                        rowsPerPageSelected={pagination.perPage}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        showRowsPerPage={true}
                        forcePage={pagination.page - 1}
                        totalPages={totalPages}
                        handlePageClick={handlePageClick}
                      ></Paginator>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
            <aside className="ad-column-placeholder">
              <Tips localeManager={container.localeManager}></Tips>
              {!window.overwolf && (
                <BoxDynamicAd container={container} className="my-3" />
              )}
            </aside>
          </Row>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          {showBansDialog && (
            <Modal
              zIndex="999999999"
              size="lg"
              isOpen={true}
              toggle={() => toggleBansDialog(false)}
            >
              <ModalHeader
                toggle={() => toggleBansDialog(false)}
                close={
                  <Button
                    color="active-light-primary"
                    className="btn-icon btn-sm w-30px h-30px ms-auto"
                    onClick={() => toggleBansDialog(false)}
                  >
                    <FontAwesomeIcon icon="times"></FontAwesomeIcon>
                  </Button>
                }
              >
                {container.localeManager.strings.bans}
              </ModalHeader>
              <ModalBody>
                <Table
                  className="table-row-dashed table-row-gray-300 align-middle gs-0 mt-3"
                  striped
                  size="sm"
                  responsive
                  hover
                >
                  <thead>
                    <tr className="fw-bolder text-gray-700 bg-light">
                      <th scope="col" className="ps-4">
                        {container.localeManager.strings.user}
                      </th>
                      <th scope="col">
                        {container.localeManager.strings.issuedBy2}
                      </th>
                      <th scope="col">
                        {container.localeManager.strings.date}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bans.map((m) => {
                      return (
                        <tr>
                          <td className="ps-4">{m.user.name}</td>
                          <td className="ps-1">{m.issued_by.name}</td>
                          <td className="ps-1">
                            {container.localeManager
                              .moment(m.created_at)
                              .format("L")}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              onClick={() => removeBan(m.id)}
                              className="ms-3 btn-icon w-30px h-30px ms-auto"
                              color="active-light-danger"
                              title={container.localeManager.strings.removeBan}
                            >
                              <FontAwesomeIcon
                                className="me-0"
                                icon="trash-alt"
                              ></FontAwesomeIcon>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </ModalBody>
            </Modal>
          )}
        </Col>
      </Row>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </LoadingOverlay>
  );
};

export default ApplicationsManagement;
